import { useCallback } from 'react';
import { Measure } from '../types';

interface MeasureEditDeps {
  updateSourceChart: (updater: (prev: any[]) => any[]) => void;
  setNumberOfMeasures: (updater: (prev: number) => number) => void;
  currentMeasure: number | null;
  setCurrentMeasure: (measure: number | null) => void;
  setCurrentChordIndex: (index: number | null) => void;
  setIsPlaying: (playing: boolean) => void;
  startMeasureIndex: number;
  setStartMeasureIndex: (updater: (prev: number) => number) => void;
  modals: {
    openInsertMeasuresDialog: (afterMeasureIndex: number) => void;
    closeInsertMeasuresDialog: () => void;
    openDeleteMeasureDialog: (measureIndex: number) => void;
    closeDeleteMeasureDialog: () => void;
  };
}

export function useMeasureEdit(deps: MeasureEditDeps) {
  const {
    updateSourceChart, setNumberOfMeasures,
    currentMeasure, setCurrentMeasure, setCurrentChordIndex, setIsPlaying,
    startMeasureIndex, setStartMeasureIndex,
    modals,
  } = deps;

  const handleInsertMeasures = useCallback((afterMeasureIndex: number) => {
    modals.openInsertMeasuresDialog(afterMeasureIndex);
  }, []);

  const handleInsertMeasuresConfirm = useCallback((afterMeasureIndex: number, count: number) => {
    const newMeasures: Measure[] = Array(count).fill(null).map(() => ({
      id: Date.now() + Math.random(),
      chords: [null],
      lyrics: ''
    }));

    updateSourceChart(prevChart => {
      const newChart = [...prevChart];
      newChart.splice(afterMeasureIndex + 1, 0, ...newMeasures);
      return newChart;
    });

    setNumberOfMeasures(prev => prev + count);
    modals.closeInsertMeasuresDialog();
  }, [updateSourceChart]);

  const handleInsertMeasuresCancel = useCallback(() => {
    modals.closeInsertMeasuresDialog();
  }, []);

  const handleDeleteMeasure = useCallback((measureIndex: number) => {
    modals.openDeleteMeasureDialog(measureIndex);
  }, []);

  const handleDeleteMeasureConfirm = useCallback((measureIndex: number) => {
    updateSourceChart(prevChart => {
      if (prevChart.length <= 1) {
        return prevChart;
      }
      const newChart = [...prevChart];
      newChart.splice(measureIndex, 1);
      return newChart;
    });

    setNumberOfMeasures(prev => Math.max(1, prev - 1));

    if (currentMeasure === measureIndex) {
      setCurrentMeasure(null);
      setCurrentChordIndex(null);
      setIsPlaying(false);
    }

    if (startMeasureIndex >= measureIndex && startMeasureIndex > 0) {
      setStartMeasureIndex(prev => prev - 1);
    }

    modals.closeDeleteMeasureDialog();
  }, [updateSourceChart, currentMeasure, startMeasureIndex]);

  const handleDeleteMeasureCancel = useCallback(() => {
    modals.closeDeleteMeasureDialog();
  }, []);

  return {
    handleInsertMeasures,
    handleInsertMeasuresConfirm,
    handleInsertMeasuresCancel,
    handleDeleteMeasure,
    handleDeleteMeasureConfirm,
    handleDeleteMeasureCancel,
  };
}

export type UseMeasureEditReturn = ReturnType<typeof useMeasureEdit>;
